import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, BrainIcon, InfoIcon, ShoppingCartIcon, TrendingUpIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
const resources = [{
  title: 'Desarrollo del Ser',
  description: 'Antes de construir un negocio, necesitas construirte a ti mismo. Desarrolla tu mentalidad, disciplina y carácter para avanzar con claridad hacia tu propósito.',
  provider: 'Llados University',
  cta: 'Desarrollar mi mentalidad',
  href: 'https://www.lladosuniversity.com/a/2147553040/2ctuZFa8',
  icon: BrainIcon
}, {
  title: 'Inversión y Trading',
  description: 'Aprende sobre el mundo de las inversiones y el trading con una visión responsable, estratégica y enfocada en educación financiera.',
  provider: 'Educación en inversión y trading',
  cta: 'Aprender sobre trading',
  href: 'https://beacons.ai/indicadoresvip',
  icon: TrendingUpIcon
}, {
  title: 'Ecommerce y Negocio Digital',
  description: 'Descubre cómo iniciar un negocio digital, vender productos online y desarrollar una fuente de ingresos usando herramientas del mundo ecommerce.',
  provider: 'Academia de Gabriel Beltrán',
  cta: 'Iniciar en ecommerce',
  href: 'https://48hcash.com/',
  icon: ShoppingCartIcon
}];
export function FinanceResources() {
  const {
    ref,
    isVisible
  } = useScrollReveal({
    threshold: 0.12
  });
  return <section id="finanzas" aria-labelledby="finanzas-titulo" className="relative w-full overflow-hidden border-t border-white/5 bg-[#07101c] py-20 md:py-32" data-id="element-43">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gold/40" aria-hidden="true" data-id="element-44" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12" data-id="element-45">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 36
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto mb-12 max-w-4xl text-center md:mb-16" data-id="element-46">
          <div className="mb-6 flex items-center justify-center gap-4" data-id="element-47">
            <span className="h-px w-8 bg-gold" data-id="element-48" />
            <span className="font-montserrat text-xs uppercase tracking-[0.3em] text-gold" data-id="element-49">
              Crecimiento integral
            </span>
            <span className="h-px w-8 bg-gold" data-id="element-50" />
          </div>

          <h2 id="finanzas-titulo" className="mb-5 font-montserrat text-4xl font-extrabold tracking-[0.08em] text-warmWhite sm:text-5xl md:text-6xl" data-id="element-51">
            Finanzas
          </h2>

          <p className="mb-5 font-montserrat text-lg font-light leading-relaxed text-goldLight md:text-2xl" data-id="element-52">
            Oportunidades para crecer, emprender y desarrollar tu propósito
          </p>

          <p className="mx-auto max-w-3xl font-montserrat text-base font-light leading-relaxed text-mutedGray md:text-lg" data-id="element-53">
            En CreceElite creemos que las finanzas también forman parte del
            crecimiento integral. Por eso compartimos recursos enfocados en
            mentalidad, educación financiera y negocios digitales para ayudarte
            a avanzar con sabiduría, propósito y dirección.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-3" data-id="element-54">
          {resources.map((resource, index) => {
          const Icon = resource.icon;
          return <motion.article key={resource.title} initial={{
            opacity: 0,
            y: 42
          }} animate={isVisible ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.8,
            delay: 0.18 + index * 0.12,
            ease: [0.16, 1, 0.3, 1]
          }} className="group flex min-h-full flex-col rounded-2xl border border-white/10 bg-[#0b1625] p-7 shadow-[0_18px_45px_rgba(0,0,0,0.22)] transition-colors duration-500 hover:border-gold/50 md:p-8" data-id="element-55">
                <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-richBlack" data-id="element-56">
                  <Icon className="h-6 w-6" aria-hidden="true" data-id="element-57" />
                </div>

                <h3 className="mb-4 font-montserrat text-2xl font-semibold text-warmWhite" data-id="element-58">
                  {resource.title}
                </h3>

                <p className="mb-7 font-montserrat text-[15px] font-light leading-relaxed text-mutedGray" data-id="element-59">
                  {resource.description}
                </p>

                <div className="mb-8 border-l border-gold/50 pl-4" data-id="element-60">
                  <p className="mb-1 font-montserrat text-[10px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-61">
                    Recurso recomendado
                  </p>
                  <p className="font-montserrat text-sm leading-relaxed text-warmWhite/90" data-id="element-62">
                    {resource.provider}
                  </p>
                </div>

                <a href={resource.href} target="_blank" rel="noopener sponsored" className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gold bg-gold px-5 py-3.5 text-center font-montserrat text-sm font-semibold tracking-wide text-richBlack transition-all duration-300 hover:bg-goldLight hover:shadow-[0_0_28px_rgba(201,168,76,0.22)] focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-[#0b1625]" data-id="element-63">
                  {resource.cta}
                  <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-64" />
                </a>
              </motion.article>;
        })}
        </div>

        <motion.aside initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.62,
        ease: [0.16, 1, 0.3, 1]
      }} aria-label="Aviso sobre enlaces de afiliado" className="mx-auto mt-10 flex max-w-5xl gap-3 rounded-xl border border-white/10 bg-richBlack/40 px-5 py-4 md:mt-12 md:px-6" data-id="element-65">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-66" />
          <p className="font-montserrat text-xs font-light leading-relaxed text-mutedGray md:text-sm" data-id="element-67">
            Algunos enlaces pueden ser enlaces de afiliado. Esto significa que
            CreceElite podría recibir una comisión si decides adquirir algún
            producto o servicio, sin costo adicional para ti. Nuestra intención
            es compartir recursos que puedan ayudarte en tu desarrollo personal,
            financiero y espiritual.
          </p>
        </motion.aside>
      </div>
    </section>;
}